import React from 'react'

import { View, ViewProps } from 'tamagui'

import { Image } from '../Image'

/**
 * Displays a cover photo with a blurred+darkened copy filling any empty space.
 *
 * When the image's natural aspect ratio doesn't match the container (e.g. a 16:9
 * YouTube thumbnail in a wider card), the background blur seamlessly extends it
 * instead of leaving gaps. When the image does fill the container, the blur is
 * completely hidden behind the foreground.
 */
export const CoverPhotoWithBlurFill = ({
  src,
  alt,
  maxHeight = 200,
  borderTopLeftRadius,
  borderTopRightRadius,
}: {
  src: string
  alt: string
  maxHeight?: number
  borderTopLeftRadius?: ViewProps['borderTopLeftRadius']
  borderTopRightRadius?: ViewProps['borderTopRightRadius']
}) => {
  return (
    <View
      position="relative"
      overflow="hidden"
      width="100%"
      maxHeight={maxHeight}
      borderTopLeftRadius={borderTopLeftRadius}
      borderTopRightRadius={borderTopRightRadius}
      backgroundColor="$backgroundStrong"
      // Inline style ensures constraints apply in Shadow DOM embeds where
      // Tamagui's runtime CSS classes may not reach the shadow root.
      style={{ maxHeight, overflow: 'hidden' }}
    >
      {/* Blurred background — fills the entire container */}
      <Image
        aria-hidden
        alt=""
        src={src}
        position="absolute"
        // @ts-expect-error inset is valid CSS
        inset={0}
        width="100%"
        height="100%"
        style={{
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'blur(20px) brightness(0.3)',
          transform: 'scale(1.1)',
        }}
      />

      {/* Foreground photo — centered, height-constrained */}
      <Image
        alt={alt}
        src={src}
        width="100%"
        maxHeight={maxHeight}
        position="relative"
        style={{
          maxHeight,
          objectFit: 'contain',
          objectPosition: 'center',
        }}
      />
    </View>
  )
}
